import { Button } from 'components/ui/button';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from 'components/ui/resizable';
import { useIsMobile } from 'hooks/use-mobile';
import { MessageCircleIcon } from 'lucide-react';
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { type ImperativePanelHandle } from 'react-resizable-panels';

import { type ActiveArtifact } from '../utils/artifact-utils';
import { ArtifactViewer } from './ArtifactViewer';

export type ArtifactSplitLayoutHandle = {
  /** Give the artifact the full width (collapses the chat pane to 0). */
  expandArtifact: () => void;
};

type ArtifactSplitLayoutProps = {
  artifact: ActiveArtifact | null;
  onClose: () => void;
  /** The chat pane (transcript, composer, ...). */
  children: React.ReactNode;
};

/**
 * Chat pane + artifact viewer. With no artifact the children render alone;
 * with one, phones get a full-screen artifact layer over the chat (a 30%
 * pane of a 390px screen is unusable) and wider screens a resizable 50/50
 * split whose chat side can collapse so the artifact takes the full width.
 */
export const ArtifactSplitLayout = forwardRef<ArtifactSplitLayoutHandle, ArtifactSplitLayoutProps>(
  ({ artifact, onClose, children }, ref) => {
    const isMobile = useIsMobile();
    // Expanded = artifact pane takes the full width (chat panel collapsed to 0).
    // The chat panel is collapsed/expanded imperatively so the artifact stays
    // mounted (sandboxed iframes would reload on remount); onCollapse/onExpand
    // keep this state in sync when the user drags the handle instead.
    const [isExpanded, setIsExpanded] = useState(false);
    const chatPanelRef = useRef<ImperativePanelHandle>(null);

    const toggleExpanded = useCallback(() => {
      const panel = chatPanelRef.current;
      if (!panel) return;
      if (panel.isCollapsed()) {
        panel.expand();
      } else {
        panel.collapse();
      }
    }, []);

    useImperativeHandle(ref, () => ({
      expandArtifact: () => chatPanelRef.current?.collapse(),
    }), []);

    useEffect(() => {
      if (!artifact) setIsExpanded(false);
    }, [artifact]);

    if (!artifact) {
      return <>{children}</>;
    }

    if (isMobile) {
      return (
        <>
          {children}
          {/* Closing the layer returns to the thread, where the artifact
              card remains. */}
          <div className="fixed inset-0 z-40 flex flex-col bg-background">
            <div className="min-h-0 flex-1">
              <ArtifactViewer
                url={artifact.url}
                mediaType={artifact.mediaType}
                patches={artifact.patches}
                baseUrl={artifact.baseUrl}
                onClose={onClose}
              />
            </div>
            <div className="shrink-0 border-t border-border bg-background p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
              <Button variant="secondary" className="h-11 w-full" onClick={onClose}>
                <MessageCircleIcon className="mr-2 size-4" />
                Back to chat
              </Button>
            </div>
          </div>
        </>
      );
    }

    return (
      <ResizablePanelGroup direction="horizontal" className="min-h-0 flex-1">
        <ResizablePanel
          ref={chatPanelRef}
          defaultSize={50}
          minSize={30}
          collapsible
          collapsedSize={0}
          onCollapse={() => setIsExpanded(true)}
          onExpand={() => setIsExpanded(false)}
          className="flex flex-col"
        >
          {children}
        </ResizablePanel>
        <ResizableHandle withHandle />
        <ResizablePanel defaultSize={50} minSize={30}>
          <ArtifactViewer
            url={artifact.url}
            mediaType={artifact.mediaType}
            patches={artifact.patches}
            baseUrl={artifact.baseUrl}
            isExpanded={isExpanded}
            onToggleExpand={toggleExpanded}
            onClose={onClose}
          />
        </ResizablePanel>
      </ResizablePanelGroup>
    );
  },
);
ArtifactSplitLayout.displayName = 'ArtifactSplitLayout';
